(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  root.NaturrumConfiguratorAnalytics = api;
  api.primeCustomerPrivacyApi?.(root);
})(typeof globalThis !== 'undefined' ? globalThis : window, function () {
  const SCHEMA_VERSION = 'naturrum_configurator_analytics.v1';
  const DEBUG_STORAGE_KEY = 'naturrum_configurator_debug_events';
  const SESSION_STORAGE_KEY = 'naturrum_configurator_session_key';
  const GA4_MEASUREMENT_ID = 'G-C34P5T4LXG';
  const CUSTOMER_PRIVACY_LOAD_PROMISE_KEY = '__naturrumCustomerPrivacyApiLoadPromise';
  const CUSTOMER_PRIVACY_FEATURES = [{ name: 'consent-tracking-api', version: '0.1' }];
  const MAX_DEBUG_EVENTS = 100;
  const ATTRIBUTION_KEYS = new Set([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
  ]);
  const GA4_EVENT_NAMES = new Set([
    'naturrum_price_calculated',
    'naturrum_configured_add_to_cart',
    'naturrum_configured_checkout_started',
    'naturrum_configured_purchase',
  ]);
  const PRICE_AGGREGATE_SCHEMA_VERSION = 'naturrum_price_aggregate.v1';
  const PRICE_AGGREGATE_EVENT_NAME = 'naturrum_price_calculated_aggregate';
  const PRICE_PROTECTION_SCHEMA_VERSION = 'naturrum_price_protection.v1';
  const PRICE_PROTECTION_EVENT_NAME = 'naturrum_price_protection_event';
  const SENSITIVE_KEY_PATTERN = /(email|e-mail|mail|phone|telefon|tel|address|adresse|customer|kunde|name|navn|firstname|lastname|fornavn|efternavn|zip|postal|postnummer|city|by|note|kommentar|comment)/iu;

  function stableStringify(value) {
    if (Array.isArray(value)) {
      return `[${value.map(stableStringify).join(',')}]`;
    }

    if (value && typeof value === 'object') {
      return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
    }

    return JSON.stringify(value);
  }

  function hashString(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
  }

  function configurationId(configuration) {
    return `cfg_${hashString(stableStringify(sanitizeObject(configuration || {})))}`;
  }

  function sanitizeString(value) {
    return value.length > 180 ? `${value.slice(0, 177)}...` : value;
  }

  function sanitizeObject(value, keyName) {
    if (value == null) return value;

    if (keyName && SENSITIVE_KEY_PATTERN.test(keyName)) {
      return undefined;
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => sanitizeObject(item))
        .filter((item) => item !== undefined);
    }

    if (typeof value === 'object') {
      const output = {};
      for (const [key, item] of Object.entries(value)) {
        const cleanValue = sanitizeObject(item, key);
        if (cleanValue !== undefined) output[key] = cleanValue;
      }

      return output;
    }

    if (typeof value === 'string') return sanitizeString(value);
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean') return value;

    return undefined;
  }

  function compactObject(value) {
    const output = {};
    for (const [key, item] of Object.entries(value || {})) {
      if (item === undefined || item === null || item === '') continue;
      if (typeof item === 'object' && !Array.isArray(item) && Object.keys(item).length === 0) continue;
      output[key] = item;
    }

    return output;
  }

  function extractAttribution(location) {
    const search = location?.search || '';
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const attribution = {};

    for (const [key, value] of params.entries()) {
      if (!ATTRIBUTION_KEYS.has(key) || !value) continue;
      attribution[key] = sanitizeString(value);
    }

    return attribution;
  }

  function randomId() {
    const cryptoApi = typeof crypto !== 'undefined' ? crypto : null;
    if (cryptoApi?.randomUUID) {
      return cryptoApi.randomUUID().replace(/-/gu, '');
    }

    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }

  function hasCustomerPrivacyAllowedMethods(env) {
    return typeof env?.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
  }

  function canLoadCustomerPrivacyApi(env) {
    return typeof env?.Shopify?.loadFeatures === 'function';
  }

  function loadCustomerPrivacyApi(env) {
    if (hasCustomerPrivacyAllowedMethods(env)) return Promise.resolve(true);
    if (!canLoadCustomerPrivacyApi(env)) return Promise.resolve(false);

    const shopify = env.Shopify;
    if (shopify[CUSTOMER_PRIVACY_LOAD_PROMISE_KEY]) return shopify[CUSTOMER_PRIVACY_LOAD_PROMISE_KEY];

    shopify[CUSTOMER_PRIVACY_LOAD_PROMISE_KEY] = new Promise((resolve) => {
      try {
        shopify.loadFeatures(CUSTOMER_PRIVACY_FEATURES, (error) => {
          resolve(!error && hasCustomerPrivacyAllowedMethods(env));
        });
      } catch {
        resolve(false);
      }
    });

    return shopify[CUSTOMER_PRIVACY_LOAD_PROMISE_KEY];
  }

  function primeCustomerPrivacyApi(env) {
    loadCustomerPrivacyApi(env);
  }

  function readConsent(env) {
    const customerPrivacy = env?.Shopify?.customerPrivacy;
    const readFlag = (methodName) => {
      if (typeof customerPrivacy?.[methodName] !== 'function') return null;

      try {
        return Boolean(customerPrivacy[methodName]());
      } catch {
        return null;
      }
    };

    const analytics = readFlag('analyticsProcessingAllowed');
    const googleConsent = readGoogleConsentMode(env);
    const needsCustomerPrivacyLoad = analytics === null && canLoadCustomerPrivacyApi(env);

    return {
      analytics: analytics === true || (analytics === null && googleConsent.analytics === true),
      marketing: readFlag('marketingAllowed') === true || (googleConsent.marketing === true),
      preferences: readFlag('preferencesProcessingAllowed') === true,
      sale_of_data: readFlag('saleOfDataAllowed') === true,
      source: analytics !== null
        ? 'shopify_customer_privacy'
        : needsCustomerPrivacyLoad
          ? 'shopify_customer_privacy_unloaded'
        : googleConsent.source,
    };
  }

  function readGoogleConsentMode(env) {
    const dataLayer = Array.isArray(env?.dataLayer) ? env.dataLayer : [];
    let analytics = null;
    let marketing = null;

    for (const entry of dataLayer) {
      if (!Array.isArray(entry) || entry[0] !== 'consent') continue;
      const consent = entry[2];
      if (!consent || typeof consent !== 'object') continue;

      if (consent.analytics_storage === 'granted') analytics = true;
      if (consent.analytics_storage === 'denied') analytics = false;

      if (
        consent.ad_storage === 'granted'
        || consent.ad_user_data === 'granted'
        || consent.ad_personalization === 'granted'
      ) {
        marketing = true;
      }
      if (
        consent.ad_storage === 'denied'
        || consent.ad_user_data === 'denied'
        || consent.ad_personalization === 'denied'
      ) {
        marketing = false;
      }
    }

    return {
      analytics: analytics === true,
      marketing: marketing === true,
      source: analytics === null ? 'missing' : 'google_consent_mode',
    };
  }

  function safeStorageGet(storage, key) {
    try {
      return storage?.getItem?.(key) || null;
    } catch {
      return null;
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      storage?.setItem?.(key, value);
    } catch {
      // Storage can be blocked; tracking must not break the storefront.
    }
  }

  function sessionKey(env, options) {
    if (options?.sessionKey) return options.sessionKey;

    const storage = env?.sessionStorage;
    const existing = safeStorageGet(storage, SESSION_STORAGE_KEY);
    if (existing) return existing;

    const key = `ncs_${(options?.randomId || randomId)()}`;
    safeStorageSet(storage, SESSION_STORAGE_KEY, key);
    return key;
  }

  function buildEvent(eventName, payload, options) {
    const now = options?.now || (() => new Date());
    const idFactory = options?.randomId || randomId;
    const location = options?.location || {};
    const configuration = sanitizeObject(payload?.configuration || {});
    const product = sanitizeObject(payload?.product || {});
    const context = sanitizeObject(payload?.context || {});
    const consent = options?.consent || { analytics: true, marketing: false, preferences: false, sale_of_data: false };
    const id = payload?.configuration_id || configurationId(configuration);

    return compactObject({
      schema_version: SCHEMA_VERSION,
      event_name: eventName,
      event_id: `nca_${idFactory()}`,
      event_timestamp_iso: now().toISOString(),
      consent,
      session_key: options?.sessionKey || null,
      configuration_id: id,
      page: compactObject({
        path: location?.pathname || null,
      }),
      attribution: extractAttribution(location),
      product,
      configuration,
      context,
    });
  }

  function publishShopify(env, event) {
    const publish = env?.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return false;

    publish(event.event_name, event);
    return true;
  }

  function finiteNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function ga4ParamsFromEvent(event) {
    if (!GA4_EVENT_NAMES.has(event?.event_name)) return null;

    const product = event.product || {};
    const configuration = event.configuration || {};
    const context = event.context || {};
    const attribution = event.attribution || {};

    return compactObject({
      schema_version: event.schema_version || SCHEMA_VERSION,
      source: context.source || null,
      trigger: context.trigger || null,
      product_handle: product.product_handle || null,
      product_family: product.product_family || null,
      product_title: product.product_title || null,
      variant_id: configuration.variant_id || product.variant_id || null,
      sku: configuration.sku || product.sku || null,
      shape: configuration.shape || null,
      dimension_bucket: configuration.dimension_bucket || null,
      price_group: configuration.price_group || context.price_group || null,
      price_interval: configuration.price_interval || context.price_interval || null,
      shown_price_dkk: finiteNumber(configuration.shown_price_dkk),
      currency: configuration.currency || product.currency || null,
      length_cm: finiteNumber(configuration.length_cm),
      width_cm: finiteNumber(configuration.width_cm),
      height_cm: finiteNumber(configuration.height_cm),
      diameter_cm: finiteNumber(configuration.diameter_cm),
      is_valid_configuration: configuration.is_valid_configuration === false ? false : true,
      page_path: event.page?.path || null,
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
      utm_term: attribution.utm_term || null,
      utm_content: attribution.utm_content || null,
    });
  }

  function deliverGa4Event(env, event, options) {
    const params = ga4ParamsFromEvent(event);
    if (!params) return false;

    const payload = {
      name: event.event_name,
      params,
    };

    if (typeof options?.deliverGa4Event === 'function') {
      options.deliverGa4Event(payload);
      return true;
    }

    const directGa4 = options?.directGa4 !== false && env?.NATURRUM_CONFIGURATOR_DIRECT_GA4 !== false;
    if (directGa4 && ensureGa4Transport(env)) {
      env.gtag('event', payload.name, payload.params);
      return true;
    }

    return false;
  }

  function ensureGa4Transport(env) {
    if (!env) return false;

    if (typeof env.gtag === 'function') return true;

    env.dataLayer = Array.isArray(env.dataLayer) ? env.dataLayer : [];
    env.gtag = function gtag() {
      env.dataLayer.push(Array.from(arguments));
    };

    if (!env.NATURRUM_CONFIGURATOR_GA4_LOADED) {
      const documentRef = env.document;
      const head = documentRef?.head;
      const createElement = documentRef?.createElement;

      if (typeof createElement === 'function' && head?.appendChild) {
        const script = createElement.call(documentRef, 'script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
        head.appendChild(script);
      }

      env.gtag('js', new Date());
      env.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });
      env.NATURRUM_CONFIGURATOR_GA4_LOADED = true;
    }

    return true;
  }

  function dispatchTrackedEvent(env, event) {
    if (typeof env?.dispatchEvent !== 'function' || typeof env?.CustomEvent !== 'function') return false;

    env.dispatchEvent(new env.CustomEvent('naturrum:configurator-tracked', { detail: event }));
    return true;
  }

  function defaultStoreDebugEvent(env, event) {
    const storage = env?.localStorage;
    if (!storage) return false;

    let events = [];
    const raw = safeStorageGet(storage, DEBUG_STORAGE_KEY);
    if (raw) {
      try {
        events = JSON.parse(raw);
      } catch {
        events = [];
      }
    }

    events.push(event);
    safeStorageSet(storage, DEBUG_STORAGE_KEY, JSON.stringify(events.slice(-MAX_DEBUG_EVENTS)));
    return true;
  }

  function track(eventName, payload, options) {
    const env = options?.env || (typeof window !== 'undefined' ? window : root);
    const consent = readConsent(env);
    if (!consent.analytics) {
      if (consent.source === 'shopify_customer_privacy_unloaded' && options?.skipCustomerPrivacyApiLoad !== true) {
        loadCustomerPrivacyApi(env).then((loaded) => {
          if (!loaded) return;
          const loadedConsent = readConsent(env);
          if (!loadedConsent.analytics) return;
          track(eventName, payload, {
            ...options,
            env,
            skipCustomerPrivacyApiLoad: true,
          });
        });
      }

      return null;
    }

    const event = buildEvent(eventName, payload, {
      ...options,
      consent,
      location: options?.location || env?.location,
      sessionKey: sessionKey(env, options),
    });

    publishShopify(env, event);
    dispatchTrackedEvent(env, event);

    if (options?.debug || env?.NATURRUM_CONFIGURATOR_DEBUG === true) {
      if (typeof options?.storeDebugEvent === 'function') {
        options.storeDebugEvent(event);
      } else {
        defaultStoreDebugEvent(env, event);
      }
    }

    if (typeof options?.deliverEvent === 'function') {
      options.deliverEvent(event);
    }

    deliverGa4Event(env, event, options);

    return event;
  }

  function dateOnly(now) {
    const value = typeof now === 'function' ? now() : now;
    const date = value instanceof Date ? value : new Date(value || Date.now());
    return Number.isNaN(date.getTime()) ? new Date().toISOString().slice(0, 10) : date.toISOString().slice(0, 10);
  }

  function buildPriceAggregateEvent(payload, options) {
    const now = options?.now || (() => new Date());
    const product = sanitizeObject(payload?.product || {});
    const configuration = sanitizeObject(payload?.configuration || {});

    const event = compactObject({
      schema_version: PRICE_AGGREGATE_SCHEMA_VERSION,
      event_name: PRICE_AGGREGATE_EVENT_NAME,
      occurred_at_date: options?.occurredAtDate || dateOnly(now),
      product_handle: product.product_handle || null,
      product_family: product.product_family || null,
      product_title: product.product_title || null,
      variant_id: configuration.variant_id || product.variant_id || null,
      sku: configuration.sku || product.sku || null,
      shape: configuration.shape || null,
      price_group: configuration.price_group || null,
      price_interval: configuration.price_interval || null,
      dimension_bucket: configuration.dimension_bucket || null,
      currency: configuration.currency || product.currency || 'DKK',
    });

    if (!event.product_handle || !event.price_group || !event.price_interval || !event.dimension_bucket || !event.shape) {
      return null;
    }

    return event;
  }

  function buildPriceProtectionEvent(payload, options) {
    const now = options?.now || (() => new Date());
    const product = sanitizeObject(payload?.product || {});
    const configuration = sanitizeObject(payload?.configuration || {});
    const context = sanitizeObject(payload?.context || {});

    const event = compactObject({
      schema_version: PRICE_PROTECTION_SCHEMA_VERSION,
      event_name: PRICE_PROTECTION_EVENT_NAME,
      occurred_at_date: options?.occurredAtDate || dateOnly(now),
      event_type: payload?.event_type || context.event_type || null,
      product_handle: product.product_handle || null,
      product_family: product.product_family || null,
      product_title: product.product_title || null,
      shape: configuration.shape || null,
      price_group: configuration.price_group || null,
      price_interval: configuration.price_interval || null,
      dimension_bucket: configuration.dimension_bucket || null,
      threshold: context.threshold || null,
      reason: context.reason || null,
    });

    if (!event.event_type) return null;
    return event;
  }

  function aggregateEndpoint(env, options) {
    return String(options?.aggregateEndpoint || env?.NATURRUM_PRICE_AGGREGATE_ENDPOINT || '').trim();
  }

  function protectionEndpoint(env, options) {
    const explicit = String(options?.protectionEndpoint || env?.NATURRUM_PRICE_PROTECTION_ENDPOINT || '').trim();
    if (explicit) return explicit;

    const aggregate = aggregateEndpoint(env, options);
    if (/\/price-calculated(?:$|\?)/u.test(aggregate)) {
      return aggregate.replace('/price-calculated', '/protection-event');
    }
    return '';
  }

  function deliverAggregateEvent(env, event, options) {
    if (typeof options?.deliverAggregateEvent === 'function') {
      options.deliverAggregateEvent(event);
      return true;
    }

    const endpoint = aggregateEndpoint(env, options);
    if (!endpoint) return false;

    const body = JSON.stringify(event);
    const navigatorApi = env?.navigator;
    if (typeof navigatorApi?.sendBeacon === 'function') {
      try {
        const blob = typeof env?.Blob === 'function'
          ? new env.Blob([body], { type: 'application/json' })
          : body;
        if (navigatorApi.sendBeacon(endpoint, blob)) return true;
      } catch {
        // Fall through to fetch.
      }
    }

    if (typeof env?.fetch === 'function') {
      try {
        env.fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
          credentials: 'omit',
        })?.catch?.(() => {});
        return true;
      } catch {
        return false;
      }
    }

    return false;
  }

  function trackPriceAggregate(payload, options) {
    const env = options?.env || (typeof window !== 'undefined' ? window : root);
    const event = buildPriceAggregateEvent(payload, options);
    if (!event) return null;

    deliverAggregateEvent(env, event, options);
    return event;
  }

  function trackPriceProtection(payload, options) {
    const env = options?.env || (typeof window !== 'undefined' ? window : root);
    const event = buildPriceProtectionEvent(payload, options);
    if (!event) return null;

    deliverAggregateEvent(env, event, {
      ...options,
      aggregateEndpoint: protectionEndpoint(env, options),
    });
    return event;
  }

  function emptyFunnelRow() {
    return {
      started: 0,
      changed: 0,
      price_calculated: 0,
      valid_price_shown: 0,
      configuration_saved: 0,
      add_to_cart: 0,
      checkout_started: 0,
      purchase: 0,
      price_to_cart_dropoff: 0,
      cart_to_checkout_dropoff: 0,
      checkout_to_purchase_dropoff: 0,
    };
  }

  function funnelKey(eventName) {
    return {
      naturrum_configurator_started: 'started',
      naturrum_configurator_changed: 'changed',
      naturrum_price_calculated: 'price_calculated',
      naturrum_valid_price_shown: 'valid_price_shown',
      naturrum_configuration_saved: 'configuration_saved',
      naturrum_configured_add_to_cart: 'add_to_cart',
      naturrum_configured_checkout_started: 'checkout_started',
      naturrum_configured_purchase: 'purchase',
    }[eventName] || null;
  }

  function applyDropoffs(row) {
    const pricedConfigurations = Math.max(row.price_calculated, row.valid_price_shown);
    row.price_to_cart_dropoff = Math.max(0, pricedConfigurations - row.add_to_cart);
    row.cart_to_checkout_dropoff = Math.max(0, row.add_to_cart - row.checkout_started);
    row.checkout_to_purchase_dropoff = Math.max(0, row.checkout_started - row.purchase);
  }

  function summarizeFunnel(events) {
    const totals = emptyFunnelRow();
    const byConfiguration = {};

    for (const event of events || []) {
      const key = funnelKey(event?.event_name);
      if (!key) continue;

      const configuration = event.configuration_id || 'unknown';
      if (!byConfiguration[configuration]) byConfiguration[configuration] = emptyFunnelRow();

      totals[key] += 1;
      byConfiguration[configuration][key] += 1;
    }

    applyDropoffs(totals);
    for (const row of Object.values(byConfiguration)) applyDropoffs(row);

    return {
      totals,
      by_configuration: byConfiguration,
    };
  }

  return {
    buildEvent,
    buildPriceAggregateEvent,
    buildPriceProtectionEvent,
    configurationId,
    extractAttribution,
    ga4ParamsFromEvent,
    loadCustomerPrivacyApi,
    primeCustomerPrivacyApi,
    readConsent,
    sanitizeObject,
    summarizeFunnel,
    track,
    trackPriceAggregate,
    trackPriceProtection,
  };
});
